/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Measure from 'react-measure';
import { classnames as cx, lodash } from '@ba-ui-toolkit/utils';
import SVGIcon from '@ba-ui-toolkit/svgicon';
import ToolTip from '@ba-ui-toolkit/tooltip';
import addNewIcon from '@ba-ui-toolkit/ba-graphics/dist/icons-js/add--filled_16';
import { omit } from 'lodash';
import TabItem, { TabItemDnD } from './TabItem';
import TabScrollButton, { TabScrollButtonDnd } from './TabScrollButton';
import stringResource from './nls/StringResource';
import './Tabs.scss';

const { uniqueId } = lodash;

// TODO: might be able to export two a version of TabList(TabList & DnDTabList) later
export default class TabList extends Component {
	static propTypes = {
		draggable: PropTypes.bool,
		selectedIndex: PropTypes.number.isRequired,
		options: PropTypes.arrayOf(
			PropTypes.shape({
				value: PropTypes.string,
				label: PropTypes.string,
				icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
				isIconBeforeLabel: PropTypes.bool,
				iconPosition: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
				title: PropTypes.string,
			})
		).isRequired,
		onChange: PropTypes.func,
		onAddButtonClick: PropTypes.func,
		deleteHandler: PropTypes.func,
		moveTab: PropTypes.func,
		incrementTab: PropTypes.func,
		onDeletePrompt: PropTypes.func,
		deleteable: PropTypes.bool,
		editable: PropTypes.bool,
		multiLine: PropTypes.bool,
		vertical: PropTypes.bool,
		tabButton: PropTypes.bool,
		addable: PropTypes.bool,
		setState: PropTypes.func,
		onEditLabel: PropTypes.func,
		newTabContent: PropTypes.object,
		linePositionClass: PropTypes.string,
		addTabToolTipText: PropTypes.string,
		scrollButtonsBackgroundColor: PropTypes.string,
		scrollButtonsTextColor: PropTypes.string,
		scrollButtonsHoverColor: PropTypes.string,
		textDir: PropTypes.oneOf(['', 'ltr', 'rtl', 'auto']),
	};

	static defaultProps = {
		addTabToolTipText: stringResource.get('addNewTab'),
	};

	state = {
		lineWidth: 0,
		lineLeft: 0,
		lineWidth2: 0,
		lineLeft2: 0,
	};

	componentDidMount() {
		if (this._selectedNode) {
			this.setState({
				lineWidth: this._selectedNode.offsetWidth,
				lineLeft: this._selectedNode.offsetLeft,
				lineWidth2: this._selectedNode.offsetWidth,
				lineLeft2: this._selectedNode.offsetLeft,
			});
		}
	}

	componentDidUpdate(prevProps) {
		if (
			this._selectedNode === null ||
			this._selectedNode === -1 ||
			this._selectedNode === undefined
		) {
			return;
		}
		const newState = {
			lineWidth: this._selectedNode.offsetWidth,
			lineLeft: this._selectedNode.offsetLeft,
			lineWidth2: this._selectedNode.offsetWidth,
			lineLeft2: this._selectedNode.offsetLeft,
		};
		if (this.props.selectedIndex !== prevProps.selectedIndex) {
			// assign the line width and height based on the selected node
			this.setState(
				newState,
				// if the focus node is set, this means that focus is on a tab
				// since the selected tab has changed, we need to refocus the new selected node
				this._focusNode
					? () => {
							this._selectedNode.focus();
					  }
					: null
			);
		} else if (
			newState.lineWidth !== this.state.lineWidth ||
			newState.lineLeft !== this.state.lineLeft
		) {
			// the UI changed and we must resize the lines
			this.setState(newState);
		}

		if (this.props !== prevProps) {
			this._onScroll();
		}
	}

	_id = uniqueId('__tabList_');

	_enter = n => {
		this.setState({
			lineWidth2: n.offsetWidth,
			lineLeft2: n.offsetLeft,
		});
	};

	_leave = () => {
		this.setState({
			lineWidth2: this.state.lineWidth,
			lineLeft2: this.state.lineLeft,
		});
	};

	_onKeyDown = e => {
		const { keyCode } = e;
		const { options } = this.props;
		let i = this.props.selectedIndex;

		switch (keyCode) {
			case 36:
			// home
			// fall through
			case 38:
			// up arrow
			// fall through

			case 37:
				if (
					(keyCode === 37 && this.props.vertical) ||
					(keyCode === 38 && !this.props.vertical)
				) {
					break;
				}

				// left arrow
				if (e.altKey || keyCode === 36) {
					i = 0;
				} else {
					i -= 1;
					if (i < 0) {
						i = options.length - 1;
					}
				}
				e.preventDefault();
				break;
			case 35:
			// end
			// fall through
			case 40:
			// down arrow
			// fall through
			case 39:
				if (
					(keyCode === 39 && this.props.vertical) ||
					(keyCode === 40 && !this.props.vertical)
				) {
					break;
				}

				// right arrow
				if (e.altKey || keyCode === 35) {
					i = options.length - 1;
				} else {
					i += 1;
					if (i === options.length) {
						i = 0;
					}
				}
				e.preventDefault();
				break;
			default:
		}
		this.props.onChange(options[i].value, i);
	};

	_onClick(value, i) {
		this.props.onChange(value, i);
	}

	_onScroll = () => {
		this.setState({
			scrollLeft: this._innerNode.firstChild.scrollLeft > 4,
			scrollTop: this._innerNode.firstChild.scrollTop > 4,
			scrollRight:
				this._innerNode.firstChild.scrollWidth -
					this._innerNode.firstChild.scrollLeft -
					this._innerNode.firstChild.offsetWidth >
				4,
			scrollBottom:
				this._tabList.lastElementChild.offsetTop +
					this._tabList.lastElementChild.offsetHeight -
					(this._tabList.offsetHeight + this._innerNode.firstChild.scrollTop) >
				4,
		});
	};

	// triggering onwheel event for a custom scroll
	_onWheel = e => {
		const { deltaY } = e;
		const { deltaX } = e;
		this._innerNode.firstChild.scrollTop = this._innerNode.firstChild.scrollTop + deltaY;
		this._innerNode.firstChild.scrollLeft = this._innerNode.firstChild.scrollLeft + deltaX;
	};

	scrollList = direction => {
		if (direction === 'left') {
			this._innerNode.firstChild.scrollLeft = this._innerNode.firstChild.scrollLeft - 80;
		}
		if (direction === 'right') {
			this._innerNode.firstChild.scrollLeft = this._innerNode.firstChild.scrollLeft + 80;
		}
		if (direction === 'top') {
			this._innerNode.firstChild.scrollTop = this._innerNode.firstChild.scrollTop - 80;
		}
		if (direction === 'bottom') {
			this._innerNode.firstChild.scrollTop = this._innerNode.firstChild.scrollTop + 80;
		}
	};

	scrollTabIntoView = tab => {
		let viewableArea = this._innerNode.firstChild.getBoundingClientRect();

		if (!tab) {
			tab = this._selectedNode;
		}

		if (tab) {
			let position = tab.getBoundingClientRect();

			while (Math.floor(position.left) < Math.floor(viewableArea.left)) {
				this._innerNode.firstChild.scrollLeft = this._innerNode.firstChild.scrollLeft - 100;
				viewableArea = this._innerNode.firstChild.getBoundingClientRect();
				position = tab.getBoundingClientRect();
			}

			while (Math.floor(position.right) > Math.floor(viewableArea.right)) {
				this._innerNode.firstChild.scrollLeft = this._innerNode.firstChild.scrollLeft + 100;
				viewableArea = this._innerNode.firstChild.getBoundingClientRect();
				position = tab.getBoundingClientRect();
			}

			while (Math.floor(position.top) < Math.floor(viewableArea.top)) {
				this._innerNode.firstChild.scrollTop = this._innerNode.firstChild.scrollTop - 100;
				viewableArea = this._innerNode.firstChild.getBoundingClientRect();
				position = tab.getBoundingClientRect();
			}

			while (Math.floor(position.bottom) > Math.floor(viewableArea.bottom)) {
				this._innerNode.firstChild.scrollTop = this._innerNode.firstChild.scrollTop + 100;
				viewableArea = this._innerNode.firstChild.getBoundingClientRect();
				position = tab.getBoundingClientRect();
			}
		}
	};

	_setSelectedNode = node => {
		this._selectedNode = node;
	};

	_setFocusNode = node => {
		if (!node) node = this._selectedNode ? this._selectedNode : null;
		this._focusNode = node;
	};

	_refLine = node => {
		this._line = node;
	};

	_refLine2 = node => {
		this._line2 = node;
	};

	_refTabList = node => {
		this._tabList = node;
	};

	_refInner = node => {
		this._innerNode = node;
	};

	deleteHandler = (value, i) => {
		this.props.deleteHandler && this.props.deleteHandler(value, i);
	};

	_enableDrag = () => {
		this.setState({ dragDisabled: false });
	};

	_disableDrag = () => {
		this.setState({ dragDisabled: true });
	};

	tabLabelRef = {};

	onLabelBlur = i => () => {
		// using set state to force component to wait to update between switching between TabItem and TabItemDnD before calling .focus()
		this.setState({}, () => {
			this.tabLabelRef[i].focus();
		});
	};

	assignTabLabelRef = (ref, i) => {
		this.tabLabelRef[i] = ref;
	};

	_isDragging = value => {
		this.setState({ dragging: value });
	};

	render() {
		const {
			options,
			selectedIndex,
			onAddButtonClick,
			moveTab,
			incrementTab,
			onEditLabel,
			vertical,
			textDir,
			linePositionClass,
			draggable,
			editable,
			tabButton,
			addable,
			addTabToolTipText,
			scrollButtonsBackgroundColor,
			scrollButtonsTextColor,
			scrollButtonsHoverColor,
			onDeletePrompt,
		} = this.props;

		const { scrollLeft, scrollRight, scrollTop, scrollBottom } = this.state;

		let { deleteable } = this.props;

		const onAddButtonKeyDown = e => {
			// enter key or space bar will simulate a click
			if (e.keyCode === 13 || e.keyCode === 32) {
				e.preventDefault();
				onAddButtonClick && onAddButtonClick();
			}
		};

		let isOnlyIconClass = ' is-only-icon-tab-list';

		options.forEach(({ label }) => {
			if (label) {
				isOnlyIconClass = '';
			}
		});

		const listIsDragged = this.state.dragging;

		deleteable = deleteable && !listIsDragged;

		// Only in use TabScrollButtonDnd if drag and drop is enabled, besides that use regular TabScrollButton
		const TabScrollButtonComponent = draggable ? TabScrollButtonDnd : TabScrollButton;

		return (
			<div
				className={cx(
					`ba-common-tabList${isOnlyIconClass}`,
					{ vertical },
					{ deleteable },
					{ editable },
					{ tabButton },
					{ addable }
				)}
			>
				<Measure onResize={this._onScroll} whitelist={['width']}>
					{({ measureRef }) => (
						<div ref={measureRef}>
							<nav className="ba-common-tabList__inner" ref={this._refInner}>
								<ul
									className={`ba-common-tabList__list ${linePositionClass}`}
									onKeyDown={this._onKeyDown}
									role="tablist"
									ref={this._refTabList}
									onScroll={this._onScroll}
									onWheel={this._onWheel.bind(this)}
								>
									{this.props.options.map((props, i) => {
										const isDnD = draggable && !this.state.dragDisabled;
										const TabItems = isDnD ? TabItemDnD : TabItem;
										const tabItemDnDProps = {
											incrementTab,
											isDraggingCallback: this._isDragging,
										};

										return (
											<TabItems
												{...omit(props, 'value')}
												{...(isDnD ? tabItemDnDProps : {})}
												deleteHandler={this.deleteHandler.bind(
													this,
													props.value,
													i
												)}
												key={props.value}
												keyValue={props.value}
												textDir={textDir}
												selected={selectedIndex === i}
												editable={editable}
												deleteable={deleteable}
												vertical={vertical}
												tabButton={tabButton}
												onClick={this._onClick.bind(this, props.value, i)}
												onEnter={this._enter}
												onLeave={this._leave}
												setSelectedNode={this._setSelectedNode}
												setFocusNode={this._setFocusNode}
												disableDrag={this._disableDrag}
												enableDrag={this._enableDrag}
												index={i}
												moveTab={moveTab}
												onEditLabel={onEditLabel}
												linePositionClass={linePositionClass}
												assignTabLabelRef={ref => {
													this.tabLabelRef[i] = ref;
												}}
												onDeletePrompt={onDeletePrompt}
												onLabelBlur={this.onLabelBlur(i)}
											/>
										);
									})}
								</ul>
								{addable ? (
									<ToolTip orient="top" title={addTabToolTipText} delay>
										<SVGIcon
											aria-label={addTabToolTipText}
											iconId={addNewIcon.id}
											size="small"
											focusable
											tabIndex="0"
											className="addNewIcon"
											onKeyDown={onAddButtonKeyDown}
											onClick={onAddButtonClick}
										/>
									</ToolTip>
								) : (
									''
								)}
							</nav>
						</div>
					)}
				</Measure>
				<TabScrollButtonComponent
					scrolling={scrollBottom}
					bottom
					scrollHandler={() => this.scrollList('bottom')}
					onClick={() => this.scrollList('bottom')}
					backgroundColor={scrollButtonsBackgroundColor}
					textColor={scrollButtonsTextColor}
					hoverColor={scrollButtonsHoverColor}
				/>
				<TabScrollButtonComponent
					scrolling={scrollTop}
					top
					scrollHandler={() => this.scrollList('top')}
					onClick={() => this.scrollList('top')}
					backgroundColor={scrollButtonsBackgroundColor}
					textColor={scrollButtonsTextColor}
					hoverColor={scrollButtonsHoverColor}
				/>
				<TabScrollButtonComponent
					scrolling={scrollLeft}
					left
					scrollHandler={() => this.scrollList('left')}
					onClick={() => this.scrollList('left')}
					backgroundColor={scrollButtonsBackgroundColor}
					textColor={scrollButtonsTextColor}
					hoverColor={scrollButtonsHoverColor}
				/>
				<TabScrollButtonComponent
					scrolling={scrollRight}
					right
					scrollHandler={() => this.scrollList('right')}
					onClick={() => this.scrollList('right')}
					backgroundColor={scrollButtonsBackgroundColor}
					textColor={scrollButtonsTextColor}
					hoverColor={scrollButtonsHoverColor}
				/>
			</div>
		);
	}
}
